import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { fmtINR } from "@/lib/api";
import { Printer, FileText, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { EinvDialog, EwbDialog } from "@/components/app/GstDocs";

export default function InvoicePrint() {
  const { id } = useParams();
  const [t, setT] = useState(null);
  const [firm, setFirm] = useState(null);
  const [party, setParty] = useState(null);
  const [einv, setEinv] = useState(null);
  const [ewb, setEwb] = useState(null);
  const [showEinv, setShowEinv] = useState(false);
  const [showEwb, setShowEwb] = useState(false);
  const [logo, setLogo] = useState(null);
  const [signature, setSignature] = useState(null);
  const [company, setCompany] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [tt, ff, cc] = await Promise.all([
          api.get(`/txns/${id}`),
          api.get("/firms/me"),
          api.get("/companies/current").catch(() => ({ data: null })),
        ]);
        setT(tt.data); setFirm(ff.data); setCompany(cc.data);
        if (tt.data.party_id) {
          const p = await api.get(`/parties/${tt.data.party_id}`);
          setParty(p.data);
        }
        // Fetch logo + signature (scope_query already isolates by current company)
        try {
          const [logoRes, sigRes] = await Promise.all([
            api.get("/files?kind=logo"),
            api.get("/files?kind=signature"),
          ]);
          const tok = localStorage.getItem("token");
          if (logoRes.data?.[0]) setLogo(`${API}/files/${logoRes.data[0].file_id}/download?auth=${encodeURIComponent(tok)}`);
          if (sigRes.data?.[0]) setSignature(`${API}/files/${sigRes.data[0].file_id}/download?auth=${encodeURIComponent(tok)}`);
        } catch (err) { console.error("File fetch failed:", err); }
      } catch (err) { console.error("InvoicePrint load failed:", err); }
    })();
  }, [id]);

  const loadEinv = async () => {
    try { const { data } = await api.get(`/einvoice/${id}`); setEinv(data); setShowEinv(true); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const loadEwb = async () => {
    try { const { data } = await api.get(`/ewaybill/${id}?distance_km=${t.km || 0}`); setEwb(data); setShowEwb(true); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!t || !firm) return <div className="p-6">Loading…</div>;

  // Prefer per-company branding, fall back to firm
  const brand = {
    name: company?.name || firm.firm_name,
    address: company?.address || firm.address,
    state: company?.state || firm.state,
    contact: company?.phone || firm.contact,
    email: company?.email || firm.email,
    gstin: company?.gstin || firm.gstin,
    upi_id: company?.upi_id || firm.upi_id,
    bank_details: company?.bank_details || firm.bank_details,
  };
  const template = (firm.invoice_template || "classic").toLowerCase();

  const isSale = ["sale", "pos"].includes(t.txn_type);
  const title = ({
    sale: "TAX INVOICE", pos: "POS RECEIPT", purchase: "PURCHASE BILL",
    estimate: "QUOTATION / ESTIMATE", proforma: "PROFORMA INVOICE",
    credit_note: "CREDIT NOTE", debit_note: "DEBIT NOTE",
    delivery_challan: "DELIVERY CHALLAN", expense: "EXPENSE VOUCHER",
    payment_in: "PAYMENT RECEIPT", payment_out: "PAYMENT VOUCHER",
  })[t.txn_type] || t.txn_type.toUpperCase();

  return (
    <div className="min-h-screen bg-slate-100 py-6 invoice-page-wrap">
      <div className="no-print max-w-3xl mx-auto flex justify-end mb-2 gap-2">
        {["sale", "pos", "credit_note", "debit_note"].includes(t.txn_type) && (
          <Button variant="outline" onClick={loadEinv} data-testid="einv-btn"><FileText size={14} /> E-Invoice / IRN</Button>
        )}
        {["sale", "delivery_challan", "purchase", "debit_note", "credit_note"].includes(t.txn_type) && (
          <Button variant="outline" onClick={loadEwb} data-testid="ewb-btn"><Truck size={14} /> E-Way Bill</Button>
        )}
        <Button onClick={() => { window.focus(); setTimeout(() => window.print(), 100); }} className="btn-primary" data-testid="invoice-print-btn"><Printer size={14} /> Print</Button>
      </div>
      <div className={`invoice-a4 tpl-${template} print-area`} data-testid={`invoice-tpl-${template}`}>
        <div className="invoice-header flex items-start justify-between border-b-2 border-slate-900 pb-3">
          <div className="flex items-start gap-3">
            {logo && <img src={logo} alt="logo" className="h-16 w-16 object-contain border border-slate-200 rounded" />}
            <div>
              <div className="text-[10px] tracking-widest text-slate-500 uppercase">ANV ASSOCIATE</div>
              <div className="text-2xl font-extrabold tracking-tight">{brand.name}</div>
              <div className="text-xs">{brand.address}</div>
              <div className="text-xs">{brand.state} · {brand.contact} · {brand.email}</div>
              {brand.gstin && <div className="text-xs font-mono">GSTIN: {brand.gstin}</div>}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xl font-black">{title}</div>
            <div className="text-xs mt-1 font-mono">No: {t.number}</div>
            <div className="text-xs font-mono">Date: {new Date(t.date).toLocaleDateString()}</div>
          </div>
        </div>

        {!!(t.vehicle_number || t.driver_name || t.delivery_address || t.delivery_charges) && (
          <div className="mt-3 grid grid-cols-2 gap-3 text-xs border border-slate-300 rounded p-3">
            <div className="overline">Delivery Details</div>
            <div></div>
            <div>Vehicle: <span className="font-mono">{t.vehicle_number || "—"}</span></div>
            <div>Driver: {t.driver_name || "—"} {t.km ? `· KM ${t.km}` : ""}</div>
            <div className="col-span-2">Address: {t.delivery_address || "—"}</div>
            {t.delivery_charges > 0 && <div className="col-span-2 font-semibold">Delivery Charges: {fmtINR(t.delivery_charges)}</div>}
          </div>
        )}

        {party && (
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <div className="overline">Bill To</div>
              <div className="font-semibold">{party.name}</div>
              <div className="text-xs">{party.address}</div>
              <div className="text-xs">{party.phone}</div>
              {party.gstin && <div className="text-xs font-mono">GSTIN: {party.gstin}</div>}
              {t.site && <div className="text-xs">Site: {t.site}</div>}
            </div>
            <div className="text-right">
              <div className="overline">Delivery Location</div>
              <div className="text-xs">{t.delivery_address || party.address || "As per bill"}</div>
            </div>
          </div>
        )}

        <div className="mt-4">
          <table className="acc-table w-full">
            <thead>
              <tr><th>#</th><th>Item</th><th>HSN</th><th className="num">Qty</th><th>Unit</th><th className="num">Price</th><th className="num">Disc</th>{t.gst_enabled && <th className="num">GST%</th>}<th className="num">Amount</th></tr>
            </thead>
            <tbody>
              {t.lines.map((l, i) => {
                const amt = (l.qty * l.price) - (l.discount || 0);
                return (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td>{l.name}</td>
                    <td className="font-mono text-xs">{l.hsn || "—"}</td>
                    <td className="num">{l.qty}</td>
                    <td>{l.unit}</td>
                    <td className="num">{fmtINR(l.price)}</td>
                    <td className="num">{fmtINR(l.discount)}</td>
                    {t.gst_enabled && <td className="num">{l.gst_rate}%</td>}
                    <td className="num">{fmtINR(amt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-4">
          <div className="text-xs text-slate-700">
            {t.notes && <div><span className="overline">Notes: </span>{t.notes}</div>}
            {isSale && t.balance > 0 && (
              <div className="mt-2 border border-amber-300 bg-amber-50 rounded p-2">
                <div className="overline text-amber-800">Payment Pending</div>
                <div>Pending: <span className="font-mono font-bold">{fmtINR(t.balance)}</span></div>
                {t.credit_period_days > 0 && <div>Credit period: {t.credit_period_days} days</div>}
              </div>
            )}
            {/* UPI QR — show prominently when payment_mode is UPI, or as a
                "Pay via UPI" prompt when there's an outstanding balance. */}
            {brand.upi_id && (t.payment_mode === "upi" || t.balance > 0) && (
              <div className={`mt-2 rounded p-2 ${t.payment_mode === "upi" ? "border-2 border-[#064E3B] bg-[#ECFDF5]" : "border border-slate-200"}`} data-testid="invoice-upi-qr-block">
                <div className={`overline ${t.payment_mode === "upi" ? "text-[#064E3B]" : ""}`}>
                  {t.payment_mode === "upi" ? "Scan to Pay via UPI" : "Pay via UPI"}
                </div>
                <div className="flex items-center gap-3">
                  <div className="bg-white border border-slate-300 rounded p-1">
                    <QRCodeCanvas value={`upi://pay?pa=${brand.upi_id}&pn=${encodeURIComponent(brand.name)}&am=${t.balance || t.total}&cu=INR&tn=${t.number}`} size={t.payment_mode === "upi" ? 140 : 110} />
                  </div>
                  <div>
                    <div className="font-mono text-xs font-semibold">{brand.upi_id}</div>
                    <div className="text-[10px] text-slate-500">Scan with any UPI app (GPay, PhonePe, Paytm…)</div>
                    <div className="text-[10px] mt-1 font-mono">Amount: {fmtINR(t.balance || t.total)}</div>
                    {t.payment_mode === "upi" && (
                      <div className="text-[10px] mt-1 text-[#064E3B] font-bold uppercase">UPI Payment Mode Selected</div>
                    )}
                  </div>
                </div>
              </div>
            )}
            {brand.bank_details?.account_number && (
              <div className="mt-2">
                <div className="overline">Bank Details</div>
                <div className="text-xs">{brand.bank_details.name} · A/c {brand.bank_details.account_number} · IFSC {brand.bank_details.ifsc}</div>
              </div>
            )}
          </div>
          <div className="border border-slate-300 rounded p-3 space-y-1 font-mono text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span>{fmtINR(t.subtotal)}</span></div>
            <div className="flex justify-between"><span>Discount</span><span>-{fmtINR(t.total_discount)}</span></div>
            {t.gst_enabled && t.cgst > 0 && <div className="flex justify-between"><span>CGST</span><span>{fmtINR(t.cgst)}</span></div>}
            {t.gst_enabled && t.sgst > 0 && <div className="flex justify-between"><span>SGST</span><span>{fmtINR(t.sgst)}</span></div>}
            {t.gst_enabled && t.igst > 0 && <div className="flex justify-between"><span>IGST</span><span>{fmtINR(t.igst)}</span></div>}
            {t.delivery_charges > 0 && <div className="flex justify-between"><span>Delivery Charges</span><span>{fmtINR(t.delivery_charges)}</span></div>}
            <div className="flex justify-between font-bold text-base border-t border-slate-400 pt-1 mt-1"><span>Total</span><span>{fmtINR(t.total)}</span></div>
            <div className="flex justify-between"><span>Paid ({t.payment_mode || "—"})</span><span>{fmtINR(t.paid)}</span></div>
            <div className="flex justify-between font-bold"><span>Balance</span><span>{fmtINR(t.balance)}</span></div>
          </div>
        </div>

        {/* Signature block */}
        <div className="mt-10 grid grid-cols-2 gap-8 text-xs">
          <div>
            <div className="border-t border-slate-400 pt-1 text-center">
              <div className="font-semibold">Receiver's Signature</div>
              <div className="text-[10px] text-slate-500">Goods received in good condition</div>
            </div>
          </div>
          <div>
            <div className="border-t border-slate-400 pt-1 text-center">
              {signature && <img src={signature} alt="sig" className="h-12 object-contain mx-auto mb-1" />}
              <div className="font-semibold">For {brand.name}</div>
              <div className="text-[10px] text-slate-500">Authorised Signatory</div>
            </div>
          </div>
        </div>

        <div className="mt-6 border-t border-slate-300 pt-3 text-center text-xs text-slate-600">
          Thank you for your business!
          {brand.contact && <div className="mt-1">For queries: <b>{brand.name}</b> · {brand.contact}</div>}
        </div>
      </div>

      <EinvDialog open={showEinv} onClose={() => setShowEinv(false)} t={t} data={einv} onRefresh={loadEinv} />
      <EwbDialog open={showEwb} onClose={() => setShowEwb(false)} t={t} data={ewb} onRefresh={loadEwb} />
    </div>
  );
}
